import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Download, 
  BookOpen, 
  Video, 
  FileText, 
  Calculator,
  CheckCircle,
  TrendingUp,
  Search,
  Target,
  Users,
  ShoppingCart,
  Package,
  Loader2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";

interface Resource {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  type: string;
  price: number; // in pence
  features?: string[];
}

const Resources = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loadingResource, setLoadingResource] = useState<string | null>(null);
  const [purchasedResources, setPurchasedResources] = useState<string[]>([]);

  const paidResources: Resource[] = [
    {
      id: "roi-calculator",
      icon: Calculator,
      title: "B2B ROI Calculator Pro",
      description: "Advanced ROI calculator with industry benchmarks, multi-channel attribution, and 12-month projections for B2B campaigns.",
      type: "Interactive Tool + Guide",
      price: 999,
      features: ["Multi-channel ROI tracking", "Industry benchmarks", "12-month projections", "Custom scenarios"]
    },
    {
      id: "seo-checklist",
      icon: FileText,
      title: "Enterprise SEO Audit Checklist",
      description: "100-point technical SEO checklist used by our team. Covers Core Web Vitals, schema markup, competitor analysis.",
      type: "PDF Checklist",
      price: 999,
      features: ["100+ audit points", "Priority scoring", "Implementation guide", "Monthly tracking template"]
    },
    {
      id: "lead-gen-playbook",
      icon: BookOpen,
      title: "B2B Lead Generation Playbook",
      description: "Complete system for generating qualified B2B leads through LinkedIn, content marketing, and paid advertising.",
      type: "eBook + Templates",
      price: 999,
      features: ["LinkedIn outreach scripts", "Content calendar template", "Ad copy frameworks", "CRM setup guide"]
    },
    {
      id: "local-seo-template",
      icon: Target,
      title: "Local SEO Domination Kit",
      description: "Step-by-step templates for dominating local search. GMB optimization, citation building, review management.",
      type: "Template Pack",
      price: 999,
      features: ["GMB optimization guide", "50+ citation sources", "Review request templates", "Local schema markup"]
    },
    {
      id: "sales-funnel-blueprint",
      icon: TrendingUp,
      title: "High-Converting Funnel Blueprint",
      description: "The exact funnel frameworks we use to achieve 15%+ conversion rates for B2B service businesses.",
      type: "Blueprint + Wireframes",
      price: 999,
      features: ["5 proven funnel templates", "Wireframes & copy", "A/B testing framework", "Automation setup"]
    }
  ];

  const bundlePrice = 2999;

  useEffect(() => {
    // Check for successful purchase return
    const sessionId = searchParams.get('session_id');
    const resourceId = searchParams.get('resource_id');
    
    if (sessionId && resourceId) {
      verifyPurchase(sessionId, resourceId);
    }
  }, [searchParams]);

  const verifyPurchase = async (sessionId: string, resourceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-resource-purchase', {
        body: { sessionId, resourceId }
      });

      if (error) throw error;

      if (data.verified) {
        setPurchasedResources(prev => [...prev, resourceId]);
        if (data.email) {
          localStorage.setItem('resource_purchase_email', data.email);
        }
        toast({
          title: "Purchase Successful!",
          description: `Thank you for your purchase. Your download is now available.`,
        });
      }
    } catch (error) {
      console.error('Error verifying purchase:', error);
      toast({
        title: "Verification Issue",
        description: "Please contact support if you need assistance with your purchase.",
        variant: "destructive"
      });
    }
  };

  const handlePurchase = async (resource: Resource) => {
    setLoadingResource(resource.id);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-resource-checkout', {
        body: {
          resourceId: resource.id,
          resourceName: resource.title,
          price: resource.price,
          successUrl: `${window.location.origin}/resources`,
          cancelUrl: `${window.location.origin}/resources`
        }
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Unable to start checkout. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingResource(null);
    }
  };

  const handleBundlePurchase = async () => {
    setLoadingResource('bundle');
    
    try {
      const { data, error } = await supabase.functions.invoke('create-resource-checkout', {
        body: {
          resourceId: 'complete-bundle',
          resourceName: 'Complete B2B Growth Resource Bundle (5 Resources)',
          price: bundlePrice,
          successUrl: `${window.location.origin}/resources`,
          cancelUrl: `${window.location.origin}/resources`
        }
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating bundle checkout:', error);
      toast({
        title: "Error",
        description: "Unable to start checkout. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingResource(null);
    }
  };

  const handleDownload = async (resource: Resource) => {
    setLoadingResource(resource.id);
    
    try {
      // Get email from localStorage or prompt
      const storedEmail = localStorage.getItem('resource_purchase_email');
      const email = storedEmail || prompt('Please enter the email you used for purchase:');
      
      if (!email) {
        toast({
          title: "Email Required",
          description: "Please enter your purchase email to download.",
          variant: "destructive"
        });
        return;
      }

      localStorage.setItem('resource_purchase_email', email);

      const { data, error } = await supabase.functions.invoke('generate-resource-pdf', {
        body: { resourceId: resource.id, email }
      });

      if (error) throw error;

      if (data.pdf) {
        // Convert base64 to blob and download
        const byteCharacters = atob(data.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `VGP-${resource.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: "Download Complete",
          description: `${resource.title} has been downloaded.`,
        });
      }
    } catch (error: any) {
      console.error('Error downloading:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Unable to download. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingResource(null);
    }
  };

  const formatPrice = (pence: number) => {
    return `£${(pence / 100).toFixed(2)}`;
  };

  const guides = [
    {
      title: "The Complete Guide to Local SEO for Service Businesses",
      description: "Learn how to dominate local search results and attract more customers in your area.",
      readTime: "12 min read",
      category: "SEO"
    },
    {
      title: "How to Create High-Converting Landing Pages",
      description: "Best practices for designing landing pages that turn visitors into leads and customers.",
      readTime: "8 min read", 
      category: "Conversion"
    },
    {
      title: "Google Ads vs. Facebook Ads: Which is Right for Your Business?",
      description: "Compare the pros and cons of each platform to make the best choice for your marketing budget.",
      readTime: "10 min read",
      category: "Paid Ads"
    },
    {
      title: "Marketing Automation for B2B Businesses",
      description: "How to set up automated marketing systems that nurture leads and increase sales.",
      readTime: "15 min read",
      category: "Automation"
    }
  ];

  const videoResources = [
    {
      title: "SEO Fundamentals: Getting Started",
      description: "Learn the basics of SEO and how to improve your website's search rankings.",
      duration: "25 minutes"
    },
    {
      title: "Google Ads Setup Walkthrough",
      description: "Step-by-step guide to setting up your first Google Ads campaign.",
      duration: "18 minutes"
    },
    {
      title: "Content Marketing Strategy",
      description: "How to create and distribute content that attracts and converts customers.",
      duration: "22 minutes"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary via-primary to-primary-light">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              B2B Growth <span className="text-secondary">Resources</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              Premium tools, templates, and playbooks we use to generate 6-figure pipelines for our clients. 
              Get instant access to battle-tested B2B growth frameworks.
            </p>
          </div>
        </div>
      </section>

      {/* Bundle Offer Section */}
      <section className="py-12 bg-gradient-to-r from-secondary/10 to-primary/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-secondary shadow-luxury">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary/70 rounded-lg flex items-center justify-center">
                      <Package className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">Complete B2B Growth Bundle</h2>
                      <p className="text-muted-foreground">All 5 premium resources • Save £20</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground line-through">£49.95</p>
                      <p className="text-3xl font-bold text-secondary">{formatPrice(bundlePrice)}</p>
                    </div>
                    <Button 
                      variant="premium" 
                      size="lg"
                      onClick={handleBundlePurchase}
                      disabled={loadingResource === 'bundle'}
                    >
                      {loadingResource === 'bundle' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-4 h-4 mr-2" />
                      )}
                      Get Bundle
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Premium Resources Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Premium <span className="text-secondary">Resources</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                The exact frameworks and tools we use to generate results for B2B clients
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paidResources.map((resource) => {
                const isPurchased = purchasedResources.includes(resource.id) || 
                                   purchasedResources.includes('complete-bundle');
                
                return (
                  <Card key={resource.id} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 hover:shadow-luxury hover:-translate-y-2 flex flex-col">
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                        <resource.icon className="w-8 h-8 text-secondary" />
                      </div>
                      <div className="inline-block bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-medium mb-2">
                        {resource.type}
                      </div>
                      <CardTitle className="text-xl text-foreground">{resource.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center flex-1 flex flex-col">
                      <p className="text-muted-foreground mb-4">{resource.description}</p>
                      
                      {resource.features && (
                        <ul className="text-left text-sm text-muted-foreground space-y-2 mb-6">
                          {resource.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="mt-auto">
                        <p className="text-2xl font-bold text-foreground mb-4">{formatPrice(resource.price)}</p>
                        
                        {isPurchased ? (
                          <Button 
                            variant="premium" 
                            size="sm" 
                            className="w-full"
                            onClick={() => handleDownload(resource)}
                            disabled={loadingResource === resource.id}
                          >
                            {loadingResource === resource.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Download Now
                          </Button>
                        ) : (
                          <Button 
                            variant="premium" 
                            size="sm" 
                            className="w-full"
                            onClick={() => handlePurchase(resource)}
                            disabled={loadingResource === resource.id}
                          >
                            {loadingResource === resource.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <ShoppingCart className="w-4 h-4 mr-2" />
                            )}
                            Buy Now
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Free Guides Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Free Expert <span className="text-secondary">Guides</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                In-depth articles and strategies from our B2B growth experts
              </p>
            </div>

            <div className="space-y-6">
              {guides.map((guide, index) => (
                <Card key={index} className="border-0 shadow-elegant hover:shadow-luxury transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-4">
                          <span className="bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-medium mr-3">
                            {guide.category}
                          </span>
                          <span className="text-sm text-muted-foreground">{guide.readTime}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-foreground mb-3">{guide.title}</h3>
                        <p className="text-muted-foreground mb-4">{guide.description}</p>
                        <Button 
                          variant="ghost" 
                          className="text-secondary hover:text-secondary"
                          onClick={() => toast({ title: "Coming Soon", description: "This guide will be available soon." })}
                        >
                          Read Guide →
                        </Button>
                      </div>
                      <BookOpen className="w-8 h-8 text-secondary ml-6 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Resources Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Video <span className="text-secondary">Tutorials</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Learn through step-by-step video walkthroughs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {videoResources.map((video, index) => (
                <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 hover:shadow-luxury">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mb-4">
                      <Video className="w-6 h-6 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">{video.title}</h3>
                    <p className="text-muted-foreground mb-4">{video.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{video.duration}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-secondary hover:text-secondary"
                        onClick={() => toast({ title: "Coming Soon", description: "This video will be available soon." })}
                      >
                        Watch Now →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-light">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need a Custom Growth Strategy?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              While our resources are powerful, nothing beats a custom strategy 
              tailored specifically to your B2B business. Schedule a strategic review.
            </p>
            <Button variant="premium" size="xl" asChild>
              <a href="/do-you-qualify">
                Schedule Strategic Review
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
